import { Copy, Highlighter, Strikethrough, Underline } from 'lucide-react'
import { useEffect, useState } from 'react'
import { getPageSelection, markupSelection } from '@/lib/selection'
import { toast } from '@/store/feedbackStore'
import { useUi } from '@/store/uiStore'

/** PDF Expert-style popup over selected text: Copy · Highlight · Underline · Strike. */
export function SelectionMenu() {
  const [pos, setPos] = useState<{ x: number; y: number; below: boolean } | null>(null)
  const tool = useUi((s) => s.tool)

  useEffect(() => {
    let t: ReturnType<typeof setTimeout>
    let pointerDown = false
    const update = () => {
      clearTimeout(t)
      t = setTimeout(() => {
        if (pointerDown) return
        const sel = getPageSelection()
        if (!sel || !sel.toString().trim()) return setPos(null)
        const r = sel.getRangeAt(0).getBoundingClientRect()
        if (!r.width && !r.height) return setPos(null)
        const below = r.top < 120
        setPos({ x: r.left + r.width / 2, y: below ? r.bottom + 10 : r.top - 10, below })
      }, 120)
    }
    const down = () => {
      pointerDown = true
    }
    const up = () => {
      pointerDown = false
      update()
    }
    document.addEventListener('selectionchange', update)
    window.addEventListener('pointerdown', down, true)
    window.addEventListener('pointerup', up, true)
    const hide = () => setPos(null)
    window.addEventListener('scroll', hide, true)
    return () => {
      clearTimeout(t)
      document.removeEventListener('selectionchange', update)
      window.removeEventListener('pointerdown', down, true)
      window.removeEventListener('pointerup', up, true)
      window.removeEventListener('scroll', hide, true)
    }
  }, [])

  if (!pos) return null
  if (tool === 'highlight' || tool === 'underline' || tool === 'strikeout') {
    // desktop: the markup is applied on mouse-up; touch users still get the menu
    if (!matchMedia('(pointer: coarse)').matches) return null
  }

  const act = (fn: () => void) => (e: React.PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    fn()
    setPos(null)
  }
  const btn =
    'flex h-9 items-center gap-1.5 rounded-xl px-2.5 text-[13px] font-medium hover:bg-white/10 active:bg-white/15'

  return (
    <div
      role="toolbar"
      aria-label="Selected text"
      className="fixed z-50 flex animate-pop items-center gap-0.5 rounded-2xl bg-[#1d2130] p-1 text-white shadow-lg"
      style={{
        left: Math.max(8, Math.min(window.innerWidth - 8, pos.x)),
        top: pos.y,
        transform: `translate(-50%, ${pos.below ? '0' : '-100%'})`,
      }}
      onPointerDown={(e) => e.preventDefault()}
    >
      <button
        type="button"
        className={btn}
        onPointerDown={act(() => {
          const text = window.getSelection()?.toString() ?? ''
          navigator.clipboard?.writeText(text).then(
            () => toast('Copied'),
            () => document.execCommand('copy'),
          )
        })}
      >
        <Copy className="size-4" /> Copy
      </button>
      <span className="mx-0.5 h-5 w-px bg-white/15" />
      <button
        type="button"
        className={btn}
        aria-label="Highlight"
        onPointerDown={act(() => markupSelection('highlight'))}
      >
        <Highlighter className="size-4 text-yellow-300" />
        <span className="max-sm:hidden">Highlight</span>
      </button>
      <button
        type="button"
        className={btn}
        aria-label="Underline"
        onPointerDown={act(() => markupSelection('underline'))}
      >
        <Underline className="size-4 text-green-300" />
      </button>
      <button
        type="button"
        className={btn}
        aria-label="Strikethrough"
        onPointerDown={act(() => markupSelection('strikeout'))}
      >
        <Strikethrough className="size-4 text-red-300" />
      </button>
    </div>
  )
}
